import { queryOptions } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { saveBlob } from '../../lib/save-file'
import type {
  Catalog,
  Dashboard,
  Delivery,
  DigestKind,
  FilterOptions,
  GstFiling,
  GstPeriodRow,
  GstRegistration,
  GstValidation,
  Gstr1,
  Gstr3b,
  ReportExport,
  ReportResult,
  ReportView,
  Subscription,
  SubscriptionsMeta,
} from './types'

const data = <T>(p: Promise<{ data: { data: T } }>): Promise<T> => p.then((r) => r.data.data)

export type Params = Record<string, string | number | boolean | null | undefined>
export const clean = (params: Params): Record<string, string | number | boolean> =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ) as Record<string, string | number | boolean>

export const rk = {
  catalog: ['reports', 'catalog'] as const,
  options: ['reports', 'options'] as const,
  run: (key: string, params: Params) => ['reports', 'run', key, clean(params)] as const,
  exports: ['reports', 'exports'] as const,
  dashboard: (asOf?: string) => ['dashboard', asOf ?? 'today'] as const,
  gstRegistrations: ['gst', 'registrations'] as const,
  gstPeriods: (reg: number) => ['gst', 'periods', reg] as const,
  gst: (kind: string, reg: number, period: string) => ['gst', kind, reg, period] as const,
  subscriptions: ['report-subscriptions'] as const,
  telegram: ['settings', 'telegram'] as const,
}

export const reportsApi = {
  catalog: () => data<Catalog>(api.get('/reports/catalog')),
  options: () => data<FilterOptions>(api.get('/reports/options')),
  run: (key: string, params: Params) =>
    data<ReportResult>(api.get(`/reports/run/${key}`, { params: clean(params) })),
  favourite: (key: string, on: boolean) =>
    on ? api.post(`/reports/favourites/${key}`) : api.delete(`/reports/favourites/${key}`),
  saveView: (body: { report_key: string; name: string; filters: Params }) =>
    data<ReportView>(api.post('/report-views', { ...body, filters: clean(body.filters) })),
  deleteView: (id: number) => api.delete(`/report-views/${id}`),
  exports: () => data<ReportExport[]>(api.get('/report-exports')),
  exportStatus: (id: number) => data<ReportExport>(api.get(`/report-exports/${id}`)),
}

export const catalogQuery = queryOptions({
  queryKey: rk.catalog,
  queryFn: reportsApi.catalog,
  staleTime: 60_000,
})
export const optionsQuery = queryOptions({
  queryKey: rk.options,
  queryFn: reportsApi.options,
  staleTime: 300_000,
})

export const dashboardApi = {
  get: (asOf?: string) => data<Dashboard>(api.get('/dashboard', { params: clean({ as_of: asOf }) })),
}

export const gstApi = {
  registrations: () => data<GstRegistration[]>(api.get('/gst/registrations')),
  periods: (reg: number) =>
    data<GstPeriodRow[]>(api.get('/gst/periods', { params: { gst_registration_id: reg } })),
  validation: (reg: number, period: string) =>
    data<GstValidation>(api.get('/gst/validation', { params: { gst_registration_id: reg, period } })),
  gstr1: (reg: number, period: string) =>
    data<Gstr1>(api.get('/gst/gstr1', { params: { gst_registration_id: reg, period } })),
  gstr3b: (reg: number, period: string) =>
    data<Gstr3b>(api.get('/gst/gstr3b', { params: { gst_registration_id: reg, period } })),
  file: (body: {
    gst_registration_id: number
    period: string
    return_type: 'gstr1' | 'gstr3b'
    arn?: string
    notes?: string
  }) => data<GstFiling>(api.post('/gst/filings', body)),
  reopen: (id: number, reason: string) => data<GstFiling>(api.post(`/gst/filings/${id}/reopen`, { reason })),
}

export const subscriptionsApi = {
  list: () =>
    api.get<{ data: Subscription[]; meta: SubscriptionsMeta }>('/report-subscriptions').then((r) => r.data),
  create: (body: Partial<Subscription>) => data<Subscription>(api.post('/report-subscriptions', body)),
  update: (id: number, body: Partial<Subscription>) =>
    data<Subscription>(api.patch(`/report-subscriptions/${id}`, body)),
  remove: (id: number) => api.delete(`/report-subscriptions/${id}`),
  sendNow: (id: number) => data<{ deliveries: Delivery[] }>(api.post(`/report-subscriptions/${id}/send-now`)),
  preview: (kind: DigestKind) =>
    data<{ subject: string; text: string; html: string }>(
      api.get('/report-subscriptions/preview', { params: { kind } }),
    ),
  connectTelegram: (botToken: string) =>
    data<{ connected: boolean; bot_username: string | null }>(
      api.put('/settings/telegram', { bot_token: botToken }),
    ),
  disconnectTelegram: () => api.delete('/settings/telegram'),
}

/**
 * Download a file from an authenticated endpoint. Returns the queued export
 * when the server answers 202 (large exports are built in the background).
 */
export async function download(url: string, params: Params): Promise<ReportExport | null> {
  const response = await api.get<Blob>(url, { params: clean(params), responseType: 'blob' })

  if (response.status === 202) {
    const body = JSON.parse(await response.data.text()) as { data: { export: ReportExport } }
    return body.data.export
  }

  const disposition = String(response.headers['content-disposition'] ?? '')
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const name = match?.[1] ? decodeURIComponent(match[1]) : 'download'
  await saveBlob(response.data, name)

  return null
}

/** Today in local time, as yyyy-mm-dd. */
export const today = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export const lastMonth = (): string => {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() - 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
